import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Shield, Mail, Calendar, Package, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('mgr_checkout_success');
    if (!stored) {
      navigate('/', { replace: true });
      return;
    }
    try {
      setOrderData(JSON.parse(stored));
    } catch {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  if (!orderData) return null;

  const { checkout_url, items, rental_start, rental_end, rental_days, customer_email, recipient_name, pricing, security_deposit } = orderData;
  const firstName = recipient_name?.split(' ')[0] || 'there';

  const handleProceedToPayment = () => {
    sessionStorage.removeItem('mgr_checkout_success');
    window.location.assign(checkout_url);
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="checkout-success-page">
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[680px] mx-auto">

          {/* Success Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-[#C6A87C]/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-[#C6A87C]" />
            </div>
            <h1
              className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight mb-3"
              style={{ color: '#333333' }}
              data-testid="success-heading"
            >
              Gear Reserved
            </h1>
            <p className="text-base" style={{ color: '#666666' }}>
              Nice one, {firstName}. Your rental is locked in.
            </p>
          </div>

          {/* Order Summary Card */}
          <div className="border border-gray-200 mb-6" data-testid="order-summary">
            <div className="bg-[#1a1a1a] px-6 py-4">
              <h2 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest text-[#C6A87C]">
                Reservation Summary
              </h2>
            </div>
            <div className="p-6 space-y-5">
              {/* Items */}
              <div className="flex items-start gap-3">
                <Package className="w-4 h-4 text-[#C6A87C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999' }}>Gear</p>
                  {items.map((item, i) => (
                    <p key={i} className="font-semibold text-sm" style={{ color: '#333' }}>{item.title}</p>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-[#C6A87C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999' }}>Rental Period</p>
                  <p className="font-semibold text-sm" style={{ color: '#333' }}>
                    {rental_start} — {rental_end}
                    <span className="font-normal ml-2" style={{ color: '#999' }}>({rental_days} days)</span>
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#C6A87C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999' }}>Confirmation & Updates</p>
                  <p className="font-semibold text-sm" style={{ color: '#333' }}>{customer_email}</p>
                </div>
              </div>

              {/* Pricing */}
              {pricing && (
                <div className="bg-gray-50 p-4 space-y-2 text-sm border border-gray-100">
                  {pricing.rental_subtotal > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#666' }}>Rental ({rental_days} days)</span>
                      <span style={{ color: '#333' }}>${pricing.rental_subtotal?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.discount_amount > 0 && (
                    <div className="flex justify-between text-[#C6A87C]">
                      <span>Discount ({pricing.discount_percent}%)</span>
                      <span>-${pricing.discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.shipping_fee > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#666' }}>Expedited Shipping</span>
                      <span style={{ color: '#333' }}>${pricing.shipping_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.insurance_total > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#666' }}>Damage Protection</span>
                      <span style={{ color: '#333' }}>${pricing.insurance_total?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.deposit_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-700 flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Security Deposit
                      </span>
                      <span className="text-green-700">${pricing.deposit_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                    <span style={{ color: '#333' }}>Total</span>
                    <span className="text-[#C6A87C] text-lg">${pricing.total?.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Security Deposit Note */}
          {security_deposit && (
            <div
              className="bg-green-50 border border-green-200 p-5 mb-6 flex items-start gap-3"
              data-testid="deposit-note"
            >
              <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm" style={{ color: '#333' }}>
                  $150 Security Deposit — Temporary Hold Only
                </p>
                <p className="text-sm mt-1" style={{ color: '#555' }}>
                  This is a fully refundable hold, not a charge. Once your gear is returned and 
                  passes inspection, the $150 is released back to your original payment method 
                  within 5–10 business days. Simple and fair.
                </p>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-[#1a1a1a] p-6 mb-6" data-testid="whats-next">
            <h3 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest text-[#C6A87C] mb-4">
              What Happens Next
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C6A87C] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">1</span>
                <div>
                  <p className="text-white text-sm font-semibold">Complete Payment</p>
                  <p className="text-gray-400 text-xs mt-0.5">Click the button below to finalize your payment securely through Shopify.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C6A87C]/30 text-[#C6A87C] text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">2</span>
                <div>
                  <p className="text-gray-300 text-sm font-semibold">Clutch Sends You Details</p>
                  <p className="text-gray-500 text-xs mt-0.5">You'll receive pickup instructions and gear prep confirmation at {customer_email}.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C6A87C]/30 text-[#C6A87C] text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">3</span>
                <div>
                  <p className="text-gray-300 text-sm font-semibold">Gear Up & Ride</p>
                  <p className="text-gray-500 text-xs mt-0.5">Pick up your gear, hit the road, and enjoy the ride. We've got you covered.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA: Proceed to Payment */}
          <div className="text-center space-y-4">
            <Button
              onClick={handleProceedToPayment}
              className="w-full bg-[#C6A87C] hover:bg-[#b09568] text-white font-bold uppercase tracking-wider text-base py-6"
              data-testid="proceed-to-payment-btn"
            >
              Complete Payment on Shopify <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-6">
              <Link
                to="/order-status"
                className="text-sm font-medium text-[#C6A87C] hover:underline"
                data-testid="track-order-link"
              >
                Track Your Order
              </Link>
              <Link
                to="/products"
                className="text-sm font-medium hover:underline"
                style={{ color: '#999' }}
              >
                Continue Browsing
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
